import type { FechamentoImpressao } from "./historicoCaixa"

// Formatação monetária pt-BR (R$).
const formatBRL = (v: number = 0): string =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v) || 0)

const line = (n: number = 40, ch: string = "-"): string => ch.repeat(Math.max(0, n))

// Data/hora curta "dd/MM HH:mm" para o cabeçalho do fechamento.
const fmtDataHora = (ts?: number | null): string => {
    if (!ts) return "--/-- --:--"
    const d = new Date(ts)
    const dd = String(d.getDate()).padStart(2, "0")
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const hh = String(d.getHours()).padStart(2, "0")
    const mi = String(d.getMinutes()).padStart(2, "0")
    return `${dd}/${mm} ${hh}:${mi}`
}

// "Rotulo               R$ 1.234,56" - rótulo à esquerda, valor à direita (espaços).
const linhaRotuloValor = (rotulo: string, valorStr: string, largura: number): string => {
    const espacos = Math.max(1, largura - rotulo.length - valorStr.length)
    return rotulo + " ".repeat(espacos) + valorStr + "\n"
}

// "  Forma .............. R$ 98,00" - rótulo, pontos de preenchimento e valor à direita.
const linhaComPontos = (
    rotulo: string,
    valorStr: string,
    largura: number,
    indent: string = "  "
): string => {
    const base = `${indent}${rotulo} `
    const pontos = Math.max(1, largura - base.length - valorStr.length - 1)
    return `${base}${".".repeat(pontos)} ${valorStr}\n`
}

/**
 * Monta o texto do fechamento de caixa no formato de impressão a partir da estrutura
 * retornada pelo servidor (endpoint caixa/ImprimirFechamentoCaixa). Usado tanto na tela de
 * Caixa quanto no Histórico do Caixa para garantir a mesma saída.
 */
export function gerarTextoFechamento(
    fechamento: FechamentoImpressao,
    colunasPorLinha: number = 40
): string {
    const largura = colunasPorLinha > 0 ? colunasPorLinha : 40

    const cabecalho = fechamento.cabecalho || ({} as FechamentoImpressao["cabecalho"])
    const resumoGeral = fechamento.resumoGeral || ({} as FechamentoImpressao["resumoGeral"])
    const canais = fechamento.canais || []
    const porFormaPagamento = fechamento.porFormaPagamento || []
    const totais = fechamento.totais || ({} as FechamentoImpressao["totais"])

    let texto = `${line(largura, "=")}\n`
    texto += `FECHAMENTO DE CAIXA - Caixa #${cabecalho.caixaId ?? ""}\n`
    if (cabecalho.estabelecimentoNome) texto += `${cabecalho.estabelecimentoNome}\n`
    texto += `Abertura ${fmtDataHora(cabecalho.aberturaEm)}  -  Fechamento ${
        cabecalho.fechamentoEm ? fmtDataHora(cabecalho.fechamentoEm) : "em aberto"
    }\n`
    if (cabecalho.usuario) texto += `Usuario: ${cabecalho.usuario}\n`
    texto += `Impressao: ${fmtDataHora(cabecalho.dataImpressao)}\n`
    texto += `${line(largura, "=")}\n\n`

    // RESUMO GERAL
    texto += `[ RESUMO GERAL ]\n\n`
    texto += linhaRotuloValor(
        "Total bruto de vendas:",
        formatBRL(resumoGeral.totalBrutoVendas),
        largura
    )
    texto += linhaRotuloValor("Descontos:", formatBRL(resumoGeral.descontos), largura)
    texto += linhaRotuloValor("Cancelamentos:", formatBRL(resumoGeral.cancelamentos), largura)
    texto += linhaRotuloValor("Taxas de entrega:", formatBRL(resumoGeral.taxasEntrega), largura)
    texto += linhaRotuloValor("Total liquido:", formatBRL(resumoGeral.totalLiquido), largura)
    texto += "\n"
    texto += linhaRotuloValor("Qtd. pedidos:", String(resumoGeral.qtdPedidos ?? 0), largura)
    texto += linhaRotuloValor("Ticket medio:", formatBRL(resumoGeral.ticketMedio), largura)
    texto += "\n"

    // CANAIS
    canais.forEach(canal => {
        texto += `[ ${String(canal.titulo || "").toUpperCase()} ]\n`
        ;(canal.formas || []).forEach(forma => {
            texto += linhaComPontos(forma.descricao || "N/D", formatBRL(forma.valor), largura)
        })
        texto += `  ${line(largura - 2, "-")}\n`
        // iFood com cupom reembolsado: mostra Vendido / (+) Cupom / A receber
        if (canal.cupomIFood != null && canal.aReceber != null) {
            texto += linhaComPontos("Vendido", formatBRL(canal.subtotal), largura)
            texto += linhaComPontos("(+) Cupom iFood", formatBRL(canal.cupomIFood), largura)
            texto += `  ${line(largura - 2, "-")}\n`
            texto += linhaComPontos("A receber do iFood", formatBRL(canal.aReceber), largura)
        } else {
            texto += linhaComPontos("Subtotal", formatBRL(canal.subtotal), largura)
        }
        texto += "\n"
    })

    // POR FORMA DE PAGAMENTO (consolidado de todos os canais)
    if (porFormaPagamento.length) {
        texto += `[ POR FORMA DE PAGAMENTO ]\n`
        porFormaPagamento.forEach(forma => {
            texto += linhaComPontos(forma.descricao || "N/D", formatBRL(forma.valor), largura)
        })
        texto += "\n"
    }

    // TOTAIS
    texto += `${line(largura, "=")}\n`
    texto += linhaRotuloValor(
        "  TOTAL VENDIDO (cliente pagou)",
        formatBRL(totais.totalVendido),
        largura
    )
    if (totais.reembolsoIFood > 0) {
        texto += linhaRotuloValor("  (+) Reembolso iFood", formatBRL(totais.reembolsoIFood), largura)
        texto += `${line(largura, "=")}\n`
        texto += linhaRotuloValor("  TOTAL A RECEBER", formatBRL(totais.totalAReceber), largura)
    }
    texto += `${line(largura, "=")}\n`

    return texto
}
